/**
 * Modelo da visão MAPA do setor: a sala em tamanho grande com os SUBSETORES
 * e os POSTOS dentro dela, hierarquicamente (setor > subsetor > posto).
 *
 * Pedido do dono: dentro do setor ele quer organizar e reposicionar subsetores
 * e postos, desenhando à mão livre quando o formato fugir do padrão. O
 * desenho em si é de `dados/forma.js` (geometria) e `dados/tracado.js`
 * (correção do traço); aqui fica a regra da TELA:
 *
 * - de quem é a forma (setor, subsetor, posto) e onde ela pode ficar;
 * - quem é o pai de um posto (o subsetor que contém o centro dele) — é isto
 *   que faz "arrastar o posto para outro subsetor" mudar o `subsetor_id`;
 * - o que já mudou e ainda não foi gravado, e o que mandar no PUT /formas.
 *
 * Módulo puro: nada de DOM, nada de rede. A conversão de coordenadas da tela
 * para o mundo (`paraMundo`) recebe o retângulo do elemento medido pela
 * página — assim ela também é testável sem navegador.
 */
import {
  LIMITES_FORMA,
  area,
  caixaDeVarias,
  caixaEnvolvente,
  centro,
  contem,
  criarRetangulo,
  encaixarEm,
  mesmaForma,
  normalizar,
  paraPath,
  pontoDentro,
} from "../dados/forma";
import { tarefaAberta, tarefaAtrasada } from "../dados/vocabulario";
import { maiorEspacoLivre } from "../inscrita";
import { postosDoSetor, subsetoresDoSetor } from "./modelo";

/** O que o gestor está fazendo no mapa agora. */
export const MODO = Object.freeze({
  /** Selecionar, arrastar e redimensionar o que já existe. */
  ORGANIZAR: "organizar",
  /** Desenhar um subsetor novo à mão. */
  SUBSETOR: "subsetor",
  /** Desenhar um posto novo à mão. */
  POSTO: "posto",
});

export const MODOS = Object.freeze([
  { valor: MODO.ORGANIZAR, rotulo: "Organizar" },
  { valor: MODO.SUBSETOR, rotulo: "Desenhar subsetor" },
  { valor: MODO.POSTO, rotulo: "Desenhar posto" },
]);

/** Alvos que têm forma, iguais aos do PUT /formas. */
export const ALVO = Object.freeze({
  SETOR: "setor",
  SUBSETOR: "subsetor",
  POSTO: "posto",
});

/**
 * Sala de quem ainda não desenhou nada no mapa geral. Sem ela a visão Mapa
 * nasceria sem área nenhuma e o gestor não teria onde traçar o primeiro
 * subsetor — pior do que começar com um retângulo padrão que ele arrasta
 * depois no mapa geral.
 */
export const SALA_PADRAO = Object.freeze({ x: 0, y: 0, largura: 1200, altura: 800 });

/** Respiro entre a sala e a borda do quadro, na unidade do mundo. */
export const MARGEM_VISAO = 48;

/** Passo do teclado ao mover/redimensionar a seleção (setas). */
export const PASSO_TECLADO = 10;

// Folga ao comparar tamanhos: as coordenadas já vêm arredondadas a duas casas
// (`forma.js`), então "do mesmo tamanho" não pode falhar por 0,01.
const TOLERANCIA_ENCAIXE = 0.02;

const LISTA_VAZIA = Object.freeze([]);
const SEM_TEXTO = "";

// Faixa do alto do pai que os filhos automáticos não ocupam: é onde mora o
// NOME do pai. Sem ela, as bancadas nasciam por cima do nome do subsetor e a
// planta abria com um rótulo cortado.
const TOPO_LIVRE = Object.freeze({ topo: 0.24, sobra: 0.68 });

/** A forma da sala: a que o gestor desenhou no mapa geral, ou a padrão. */
export function formaDoSetor(setor) {
  return normalizar(setor?.forma) || criarRetangulo(SALA_PADRAO);
}

/** O setor ainda não tem forma própria (está usando a sala padrão). */
export function setorSemDesenho(setor) {
  return normalizar(setor?.forma) === null;
}

/**
 * `{caixa, viewBox}` do quadro: a sala mais os filhos que escaparam dela (um
 * posto arrastado para fora não pode sumir da tela), com margem.
 */
export function visaoDoMapa(formas, { margem = MARGEM_VISAO } = {}) {
  const caixa = caixaDeVarias(formas) || { ...SALA_PADRAO };
  const x = Math.max(LIMITES_FORMA.COORDENADA_MINIMA, caixa.x - margem);
  const y = Math.max(LIMITES_FORMA.COORDENADA_MINIMA, caixa.y - margem);
  const largura = caixa.x + caixa.largura + margem - x;
  const altura = caixa.y + caixa.altura + margem - y;
  return { caixa: { x, y, largura, altura }, viewBox: `${x} ${y} ${largura} ${altura}` };
}

/**
 * Quantos pixels de TELA vale uma unidade do mundo, com o mesmo
 * `xMidYMid meet` do SVG. É o que deixa alça e traço serem medidos em PIXELS
 * (44 px de alvo continuam 44 px numa sala enorme ou minúscula). Sem o
 * retângulo medido ainda, vale 1.
 */
export function escalaDaTela(retangulo, visao) {
  const caixa = visao?.caixa;
  if (!retangulo?.width || !retangulo?.height || !caixa?.largura || !caixa?.altura) return 1;
  const escala = Math.min(retangulo.width / caixa.largura, retangulo.height / caixa.altura);
  return Number.isFinite(escala) && escala > 0 ? escala : 1;
}

/**
 * Ponto da tela (clientX/clientY) em coordenadas do MUNDO.
 *
 * `retangulo` = getBoundingClientRect() do `<svg>`; `visao.caixa` = o viewBox.
 * A conta repete o `preserveAspectRatio="xMidYMid meet"` do SVG: a escala é a
 * menor das duas e o que sobra vira borda centralizada. Sem isso o traço sai
 * deslocado justamente quando o quadro não tem a proporção da sala.
 */
export function paraMundo(ponto, retangulo, visao) {
  const caixa = visao?.caixa;
  if (!ponto || !retangulo || !caixa || !retangulo.width || !retangulo.height) return null;
  const escala = Math.min(retangulo.width / caixa.largura, retangulo.height / caixa.altura);
  if (!Number.isFinite(escala) || escala <= 0) return null;
  const sobraX = (retangulo.width - caixa.largura * escala) / 2;
  const sobraY = (retangulo.height - caixa.altura * escala) / 2;
  return {
    x: caixa.x + (ponto.clientX - retangulo.left - sobraX) / escala,
    y: caixa.y + (ponto.clientY - retangulo.top - sobraY) / escala,
  };
}

// ----------------------------------------------------------------- cena

function pessoasDoPosto(posto, indice) {
  return (posto?.colaborador_ids || [])
    .map((id) => indice?.colaboradores?.get(id))
    .filter(Boolean)
    .map((c) => ({ id: c.id, nome: c.nome }));
}

function contarNoLugar({ demandas, tarefas, pecas, hoje }, casa) {
  const dosAmbientes = demandas.filter(casa);
  const daPecas = pecas.filter(casa);
  return {
    ambientes: dosAmbientes.length,
    pecas: daPecas.length,
    tarefas: tarefas.filter((t) => tarefaAberta(t) && casa(t)).length,
    atrasados: dosAmbientes.filter((d) => d.atrasada).length + daPecas.filter((p) => p.atrasada).length,
    tarefasAtrasadas: tarefas.filter((t) => tarefaAtrasada(t, hoje) && casa(t)).length,
  };
}

const subsetorDe = (item) => item.subsetorId ?? item.subsetor_id ?? null;
const postoDe = (item) => item.postoId ?? item.posto_id ?? null;

function elemento(alvo, item, forma, extras) {
  return {
    alvo,
    id: item.id,
    nome: item.nome || SEM_TEXTO,
    ativo: item.ativo !== false,
    ordem: item.ordem ?? 0,
    versao: item.versao ?? 0,
    forma,
    path: paraPath(forma),
    caixa: caixaEnvolvente(forma),
    centro: centro(forma),
    area: area(forma),
    ...extras,
  };
}

/**
 * Forma de um subsetor/posto que ainda não foi desenhado: uma caixa dentro do
 * pai, em fila, para ele APARECER no mapa em vez de ficar invisível até
 * alguém desenhar. O gestor arrasta de onde ela nasceu.
 *
 * `indice` e `total` são SEMPRE entre irmãos (os filhos do mesmo pai). Usar a
 * posição na lista do setor inteiro desenhava as bancadas de subsetores
 * diferentes umas por cima das outras, com os nomes embolados.
 */
function formaAutomatica(caixaPai, indice, total) {
  const quantos = Math.max(1, total);
  const larguraUtil = caixaPai.largura * 0.8;
  const alturaUtil = caixaPai.altura * TOPO_LIVRE.sobra;
  // Quantas FILEIRAS a altura do pai comporta de verdade. A sala da fábrica é
  // larga e baixa: uma grade quadrada (√n × √n) punha a segunda fileira de
  // bancadas para fora do subsetor, e elas abriam marcadas como "fora do
  // lugar" sem ninguém ter arrastado nada.
  const cabemEmPe = Math.max(1, Math.floor(alturaUtil / LIMITES_FORMA.LADO_MINIMO));
  const linhas = Math.max(1, Math.min(cabemEmPe, Math.ceil(Math.sqrt(quantos))));
  const colunas = Math.max(1, Math.ceil(quantos / linhas));
  const largura = Math.max(LIMITES_FORMA.LADO_MINIMO, larguraUtil / colunas);
  const altura = Math.max(LIMITES_FORMA.LADO_MINIMO, alturaUtil / linhas);
  const coluna = indice % colunas;
  const linha = Math.floor(indice / colunas);
  return criarRetangulo({
    x: caixaPai.x + caixaPai.largura * 0.1 + coluna * largura,
    y: caixaPai.y + caixaPai.altura * TOPO_LIVRE.topo + linha * altura,
    largura: largura * 0.9,
    altura: altura * 0.9,
  });
}

// Afastamento entre o que foi desenhado e o que nasce sozinho ao lado.
const FOLGA_AUTOMATICA = 8;

/** Onde nascem os não desenhados: o pai inteiro, ou o que os desenhados deixaram. */
function caixaParaAutomaticos(caixaPai, formaPai, desenhados) {
  if (!desenhados.length) return caixaPai;
  const livre = maiorEspacoLivre(caixaPai, desenhados.map(caixaEnvolvente), { dentro: formaPai, folga: FOLGA_AUTOMATICA });
  return livre && livre.largura > 0 && livre.altura > 0 ? livre : caixaPai;
}

/**
 * A cena inteira da visão Mapa.
 *
 * `entrada` = {setor, subsetores, postos, demandas, tarefas, pecas, indice,
 * hoje}; `rascunho` = o que o gestor mexeu e ainda não gravou
 * ({formas: {"alvo:id": forma}, pais: {postoId: subsetorId|null}}).
 *
 * Devolve `{setor, visao, subsetores, postos, semDesenho, fora}` — `fora` são
 * os elementos que estão pendurados fora do pai (a tela avisa e bloqueia o
 * salvamento até o gestor resolver).
 */
export function cenaDoSetor(entrada, rascunho = {}) {
  const {
    setor = null,
    subsetores: brutosSubsetores = LISTA_VAZIA,
    postos: brutosPostos = LISTA_VAZIA,
    demandas = LISTA_VAZIA,
    tarefas = LISTA_VAZIA,
    pecas = LISTA_VAZIA,
    indice = null,
    hoje = new Date(),
  } = entrada || {};
  const formasRascunho = rascunho.formas || {};
  const paisRascunho = rascunho.pais || {};
  const setorId = setor?.id || null;

  const formaSala = formasRascunho[`${ALVO.SETOR}:${setorId}`] || formaDoSetor(setor);
  const caixaSala = caixaEnvolvente(formaSala);
  const contagens = { demandas, tarefas, pecas, hoje };

  const listaSubsetores = subsetoresDoSetor(brutosSubsetores, setorId, { incluirInativos: true });
  const listaPostos = postosDoSetor(brutosPostos, setorId, { incluirInativos: true });

  // O que ainda não foi desenhado nasce no espaço que os DESENHADOS deixaram
  // livre, contado só entre os não desenhados — senão o subsetor novo caía
  // por cima de um que o gestor já tinha posto no lugar.
  const formaSalvaOuRascunho = (alvo, item) =>
    formasRascunho[`${alvo}:${item.id}`] || normalizar(item.forma) || null;
  const subsetoresDesenhados = listaSubsetores.map((s) => formaSalvaOuRascunho(ALVO.SUBSETOR, s)).filter(Boolean);
  const subsetoresSemDesenho = listaSubsetores.filter((s) => !formaSalvaOuRascunho(ALVO.SUBSETOR, s)).map((s) => s.id);
  const caixaDosSubsetoresNovos = caixaParaAutomaticos(caixaSala, formaSala, subsetoresDesenhados);

  const subsetoresCena = listaSubsetores.map((subsetor) => {
    const salva = normalizar(subsetor.forma);
    const doRascunho = formasRascunho[`${ALVO.SUBSETOR}:${subsetor.id}`] || null;
    const forma =
      doRascunho ||
      salva ||
      formaAutomatica(caixaDosSubsetoresNovos, subsetoresSemDesenho.indexOf(subsetor.id), subsetoresSemDesenho.length);
    return elemento(ALVO.SUBSETOR, subsetor, forma, {
      subsetorId: null,
      paiId: setorId,
      semDesenho: !salva,
      pendente: Boolean(doRascunho),
      pessoas: LISTA_VAZIA,
      contagem: contarNoLugar(contagens, (item) => subsetorDe(item) === subsetor.id),
      cabe: contem(formaSala, forma),
    });
  });

  const porIdSubsetor = new Map(subsetoresCena.map((s) => [s.id, s]));

  // Cada posto é colocado entre os IRMÃOS dele (os postos do mesmo subsetor,
  // ou os da fila do setor), não na lista do setor inteiro.
  const paiAgora = (posto) =>
    (posto.id in paisRascunho ? paisRascunho[posto.id] : posto.subsetor_id || null) || setorId;
  const irmaos = new Map(); // pai → {semDesenho: [ids], desenhados: [formas]}
  listaPostos.forEach((posto) => {
    const chave = paiAgora(posto);
    if (!irmaos.has(chave)) irmaos.set(chave, { semDesenho: [], desenhados: [] });
    const forma = formaSalvaOuRascunho(ALVO.POSTO, posto);
    if (forma) irmaos.get(chave).desenhados.push(forma);
    else irmaos.get(chave).semDesenho.push(posto.id);
  });

  const postosCena = listaPostos.map((posto) => {
    const paiId = posto.id in paisRascunho ? paisRascunho[posto.id] : posto.subsetor_id || null;
    const pai = porIdSubsetor.get(paiId) || null;
    const casa = irmaos.get(paiAgora(posto)) || { semDesenho: [], desenhados: [] };
    const caixaPai = caixaParaAutomaticos(pai ? pai.caixa : caixaSala, pai ? pai.forma : formaSala, casa.desenhados);
    const salva = normalizar(posto.forma);
    const doRascunho = formasRascunho[`${ALVO.POSTO}:${posto.id}`] || null;
    const forma =
      doRascunho ||
      salva ||
      formaAutomatica(caixaPai, casa.semDesenho.indexOf(posto.id), casa.semDesenho.length);
    return elemento(ALVO.POSTO, posto, forma, {
      subsetorId: paiId,
      paiId: paiId || setorId,
      paiNome: pai ? pai.nome : setor?.nome || SEM_TEXTO,
      subsetorSalvo: posto.subsetor_id || null,
      mudouDePai: (posto.subsetor_id || null) !== (paiId || null),
      semDesenho: !salva,
      pendente: Boolean(doRascunho) || posto.id in paisRascunho,
      pessoas: pessoasDoPosto(posto, indice),
      contagem: contarNoLugar(contagens, (item) => postoDe(item) === posto.id),
      cabe: contem(pai ? pai.forma : formaSala, forma),
    });
  });

  // O que escapou da sala entra na visão: um posto arrastado para fora tem
  // que continuar à vista para o gestor conseguir trazê-lo de volta.
  const visao = visaoDoMapa([formaSala, ...subsetoresCena.map((s) => s.forma), ...postosCena.map((p) => p.forma)]);

  return {
    setor: setor
      ? elemento(ALVO.SETOR, setor, formaSala, {
          etapa: setor.etapa || null,
          recebe: Boolean(setor.recebe_da_planilha),
          pendente: Boolean(formasRascunho[`${ALVO.SETOR}:${setorId}`]),
          pessoas: LISTA_VAZIA,
          contagem: contarNoLugar(contagens, () => true),
          cabe: true,
        })
      : null,
    visao,
    subsetores: subsetoresCena,
    postos: postosCena,
    semDesenho: setorSemDesenho(setor),
    fora: [...subsetoresCena, ...postosCena].filter((e) => e.ativo && !e.cabe),
  };
}

/**
 * Por que este subsetor/posto NÃO pode ser excluído — ou `null` quando pode.
 *
 * Excluir é o desfazer de quem desenhou errado, e só vale para o que NUNCA
 * carregou trabalho: com ambiente, tarefa ou peça na história a saída é
 * desativar, senão a linha do tempo passaria a falar de um lugar que não
 * existe mais. O backend confere a mesma regra (é ele quem manda); aqui a
 * regra existe para a tela não oferecer um botão que sempre falharia.
 */
export function motivoParaNaoExcluir(elemento, cena) {
  if (!elemento) return "Nada selecionado.";
  const { ambientes = 0, tarefas = 0, pecas = 0 } = elemento.contagem || {};
  const oque = elemento.alvo === ALVO.POSTO ? "posto" : "subsetor";
  if (ambientes || tarefas || pecas) {
    return `Já passou trabalho por este ${oque}: dá para desativar, não para excluir — a história fica.`;
  }
  const filhos = (cena?.postos || LISTA_VAZIA).filter((posto) => posto.subsetorId === elemento.id);
  if (elemento.alvo === ALVO.SUBSETOR && filhos.length) {
    return `Este subsetor tem ${filhos.length === 1 ? "um posto" : `${filhos.length} postos`} dentro: tire ou exclua ${filhos.length === 1 ? "ele" : "eles"} antes.`;
  }
  return null;
}

// ------------------------------------------------------------ hierarquia

/**
 * Qual subsetor recebe um posto desenhado/arrastado até ali: o que contém o
 * CENTRO da forma. Se vários contiverem (subsetores sobrepostos), vale o de
 * menor área — é o mais específico, e é o que o gestor vê por cima.
 * `null` = o posto fica na fila do setor (posto sem subsetor).
 */
export function paiDoPosto(forma, subsetores) {
  const ponto = centro(forma);
  if (!ponto) return null;
  const candidatos = (subsetores || [])
    .filter((s) => s.ativo !== false && pontoDentro(s.forma, ponto))
    .sort((a, b) => (a.area ?? 0) - (b.area ?? 0));
  return candidatos.length ? candidatos[0].id : null;
}

/**
 * O filho cabe no pai? Devolve
 * `{ok, forma, ajustada, mensagem}`:
 *
 * - cabe como está: `{ok: true, forma, ajustada: false}`;
 * - não cabe, mas cabe deslocado para dentro: encaixa e explica o que fez
 *   (princípio do menor espanto: nada some nem fica pela metade);
 * - não cabe de jeito nenhum (sala com recorte, desenho maior que o pai):
 *   `{ok: false}` com a mensagem que a tela mostra.
 */
export function validarNoPai(forma, formaPai, { nomeFilho = "Este elemento", nomePai = "a sala" } = {}) {
  const alvo = normalizar(forma);
  if (!alvo) return { ok: false, forma: null, ajustada: false, mensagem: "Não foi possível ler este desenho." };
  if (!formaPai || contem(formaPai, alvo)) return { ok: true, forma: alvo, ajustada: false, mensagem: SEM_TEXTO };
  const recusa = {
    ok: false,
    forma: alvo,
    ajustada: false,
    mensagem: `${nomeFilho} não cabe em ${nomePai}. Desenhe menor, ou dentro dela.`,
  };
  const limite = caixaEnvolvente(formaPai);
  const caixa = caixaEnvolvente(alvo);
  // Grande demais: encaixar aqui significaria ENCOLHER o desenho, o que muda o
  // que a pessoa acabou de traçar. Melhor recusar e dizer o motivo.
  if (caixa.largura > limite.largura + TOLERANCIA_ENCAIXE || caixa.altura > limite.altura + TOLERANCIA_ENCAIXE) {
    return recusa;
  }
  const encaixada = encaixarEm(alvo, limite);
  if (encaixada && contem(formaPai, encaixada)) {
    return {
      ok: true,
      forma: encaixada,
      ajustada: true,
      mensagem: `${nomeFilho} foi encaixado dentro de ${nomePai}.`,
    };
  }
  return recusa;
}

// ------------------------------------------------------------- gravação

function versaoDe(lista, id) {
  const item = (lista || []).find((x) => x.id === id);
  return item?.versao ?? 0;
}

/**
 * O que precisa ir para o backend para gravar o rascunho.
 *
 * Duas escritas diferentes, porque duas coisas mudam:
 * - `formas` → PUT /formas (tudo ou nada, com a versão de cada elemento);
 * - `reparentados` → PATCH /postos/{id} com `subsetor_id` E a forma nova.
 *
 * O posto que mudou de subsetor sai do PUT: o PATCH já bumpa a versão dele, e
 * mandar a forma nos dois lugares daria 409 garantido na segunda escrita.
 *
 * Só entra o que o gestor TOCOU (`pendente`): o subsetor que ainda não tem
 * desenho aparece numa caixa automática, e gravar isso sozinho faria a tela
 * nascer com "sem salvar" sem ninguém ter mexido em nada.
 */
export function mudancasParaSalvar(cena, { setor, subsetores, postos }) {
  const formas = [];
  const reparentados = [];

  if (cena?.setor?.pendente && !mesmaForma(setor?.forma, cena.setor.forma)) {
    formas.push({ alvo: ALVO.SETOR, id: cena.setor.id, forma: cena.setor.forma, versao: setor?.versao ?? 0 });
  }
  (cena?.subsetores || []).forEach((s) => {
    const salva = (subsetores || []).find((x) => x.id === s.id);
    if (s.pendente && !mesmaForma(salva?.forma, s.forma)) {
      formas.push({ alvo: ALVO.SUBSETOR, id: s.id, forma: s.forma, versao: versaoDe(subsetores, s.id) });
    }
  });
  (cena?.postos || []).forEach((p) => {
    if (!p.pendente) return;
    const salvo = (postos || []).find((x) => x.id === p.id);
    if (p.mudouDePai) {
      reparentados.push({
        id: p.id,
        subsetor_id: p.subsetorId || null,
        forma: p.forma,
        versao: versaoDe(postos, p.id),
      });
      return;
    }
    if (!mesmaForma(salvo?.forma, p.forma)) {
      formas.push({ alvo: ALVO.POSTO, id: p.id, forma: p.forma, versao: versaoDe(postos, p.id) });
    }
  });

  return { formas, reparentados, total: formas.length + reparentados.length };
}

/** Rascunho sem nada pendente (o botão Salvar desenho fica desabilitado). */
export const RASCUNHO_VAZIO = Object.freeze({ formas: Object.freeze({}), pais: Object.freeze({}) });

/** Chave do rascunho para um elemento ("posto:123"). */
export function chaveDoElemento(alvo, id) {
  return `${alvo}:${id}`;
}

/**
 * Rascunho com a forma nova de um elemento (e, para posto, o pai novo).
 * Função pura: devolve um rascunho novo, nunca muda o que recebeu.
 */
export function comForma(rascunho, alvo, id, forma, paiId) {
  const proximo = {
    formas: { ...(rascunho?.formas || {}), [chaveDoElemento(alvo, id)]: forma },
    pais: { ...(rascunho?.pais || {}) },
  };
  if (alvo === ALVO.POSTO && paiId !== undefined) proximo.pais[id] = paiId || null;
  return proximo;
}

/** Texto do resumo do rascunho ("2 desenhos · 1 posto trocou de subsetor"). */
export function textoDoRascunho({ formas, reparentados }) {
  const partes = [];
  if (formas.length) partes.push(`${formas.length} ${formas.length === 1 ? "desenho" : "desenhos"}`);
  if (reparentados.length) {
    partes.push(`${reparentados.length} ${reparentados.length === 1 ? "posto trocou" : "postos trocaram"} de subsetor`);
  }
  return partes.join(" · ");
}
